import { ReactNode, useContext, useEffect, useMemo, useRef } from 'react';

import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import styled from 'styled-components';

import { htmlContentStyles } from '@components/html-content';
import { IconButton } from '@components/icon-button';
import { IconAddSmall } from '@components/icons/add-small';
import { hideSidebar, useIsSidebarActive } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text, TextProps } from '@components/text';
import { FieldMappingTextEditor } from '@components/text-editors';
import {
  FieldMappingEditorValue,
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldMappingNode,
  FieldType,
  MappableVariable,
  mapVariableGroups,
  transformInput,
  useVariableStyledContent,
} from '@components/text-editors/addons/field-mapping';
import {
  FIELD_MAPPING_SIDEBAR_ID,
  useStandardFieldMappingSidebar,
} from '@components/text-editors/field-mapping-sidebar';
import { FieldMappingContext } from '@components/text-editors/helpers';
import { EditorMethods } from '@components/text-editors/text-editor';
import { theme } from '@styles/theme';
import { getRawTextFromHtml } from '@utils/browser';

import { FormBuilderContext, useFormBuilderContext } from '../context';
import { StateField } from '../state';

type FieldMappingDisplayProps = {
  content: string | FieldMappingEditorValue;
  textProps?: Omit<TextProps, 'text' | 'children'>;
  children?: ReactNode;
  variables?: Record<string, MappableVariable>;
  variableGroups?: FieldMappingGroup[];
  useRichText?: boolean;
  onRemove?: () => void;
  enableExpandable?: boolean;
  showFieldMappingSidebar?: (props?: { variable?: FieldMappingFieldItem }) => void;
};

/**
 * Renders content form fields (Heading, Subheading, Text) with blue tokens.
 * Can use FormBuilderContext or accept variables/variableGroups directly.
 *
 * For rich text content (HTML with formatting), use `useRichText={true}` to
 * preserve bold, italic, etc. while still showing blue pills for variables.
 */
export const FieldMappingDisplay = ({
  content,
  textProps,
  children,
  variables: variablesProp,
  variableGroups: variableGroupsProp,
  useRichText = false,
  onRemove,
  enableExpandable = false,
  showFieldMappingSidebar,
}: FieldMappingDisplayProps) => {
  const context = useContext(FormBuilderContext);
  const variableGroups = variableGroupsProp ?? context?.variableGroups ?? [];
  const variables = variablesProp ?? mapVariableGroups(variableGroups);

  const contentString = typeof content === 'string' ? content : content?.value ?? '';

  const embeddedVariables =
    typeof content === 'object' && content?.variables
      ? (content.variables as Record<string, MappableVariable>)
      : {};
  const mergedVariables = { ...variables, ...embeddedVariables };

  const { styledContent } = useVariableStyledContent({
    content: getRawTextFromHtml(contentString) ?? '',
    variables: mergedVariables,
    variableGroups,
    hideRemove: !onRemove,
    onRemove,
    enableExpandable,
    showFieldMappingSidebar,
  });

  if (useRichText) {
    return (
      <RichTextDisplay
        content={contentString}
        variables={mergedVariables}
        variableGroups={variableGroups}
      />
    );
  }

  return <Text {...textProps}>{children ?? styledContent}</Text>;
};

const RichTextDisplay = ({
  content,
  variables,
  variableGroups,
}: {
  content: string;
  variables: Record<string, MappableVariable>;
  variableGroups: FieldMappingGroup[];
}) => {
  const parsedContent = useMemo(
    () => transformInput({ value: content, variables, variableGroups }),
    [content, variables, variableGroups]
  );

  const editor = useEditor({
    editable: false,
    content: parsedContent.value,
    extensions: [StarterKit, Underline, Link.configure({ openOnClick: true }), FieldMappingNode],
  });

  const contextValue = useMemo(
    () => ({
      showFieldMappingSidebar: () => {},
      variableGroups,
      isEditMode: false,
    }),
    [variableGroups]
  );

  return (
    <FieldMappingContext.Provider value={contextValue}>
      <StyledDisplayEditor editor={editor} />
    </FieldMappingContext.Provider>
  );
};

const StyledDisplayEditor = styled(EditorContent)`
  .ProseMirror {
    outline: none;
    padding: 0;
    min-height: 0;
    font: ${theme.font.regular16};
  }

  ${htmlContentStyles}
`;

const CONTENT_EXCLUDED_TYPES: Set<FieldType> = new Set(['file', 'file_multi']);

const filterContentVariableGroups = (groups: FieldMappingGroup[]): FieldMappingGroup[] => {
  return groups
    .map((group) => ({
      ...group,
      fields: group.fields.filter(
        (field) =>
          !field.type || field.type === 'separator' || !CONTENT_EXCLUDED_TYPES.has(field.type)
      ),
    }))
    .filter((group) => group.fields.length > 0);
};

type UseFieldMappingEditorOptions<T extends 'content' | 'label'> = {
  field: StateField & { content?: string | FieldMappingEditorValue };
  contentKey: T;
  sidebarLabel: string;
  fieldType?: 'paragraph' | 'text';
  plaintext?: boolean;
  singleLine?: boolean;
};

export const useFieldMappingEditor = <T extends 'content' | 'label'>({
  field,
  contentKey,
  sidebarLabel,
  fieldType = 'text',
  plaintext = false,
  singleLine = false,
}: UseFieldMappingEditorOptions<T>) => {
  const { store, variableGroups: rawVariableGroups } = useFormBuilderContext();
  const variableGroups = useMemo(
    () => (rawVariableGroups ? filterContentVariableGroups(rawVariableGroups) : rawVariableGroups),
    [rawVariableGroups]
  );
  const actions = store.actions;
  const formType = store.useFormType();
  const formVariant = store.useFormVariant();
  const isFieldSelected = store.useIsFieldSelected(field.id);
  const isEditMode = store.useIsEditMode();
  const isInputForm = (formType === 'form' || formType === 'submission') && formVariant === 'input';
  const showStandardFieldMappingSidebar = useStandardFieldMappingSidebar();
  const editorRef = useRef<EditorMethods>(null);
  const isFieldMappingSidebarActive = useIsSidebarActive(FIELD_MAPPING_SIDEBAR_ID);

  const shouldUseFieldMapping = isInputForm && variableGroups && variableGroups.length > 0;

  const prevIsFieldSelectedRef = useRef(isFieldSelected);

  useEffect(() => {
    const wasSelected = prevIsFieldSelectedRef.current;

    if (wasSelected && !isFieldSelected && shouldUseFieldMapping && isFieldMappingSidebarActive) {
      hideSidebar();
    }

    prevIsFieldSelectedRef.current = isFieldSelected;
  }, [isFieldSelected, shouldUseFieldMapping, isFieldMappingSidebarActive]);

  const getFieldValue = (): FieldMappingEditorValue => {
    const value = (field as any)[contentKey];
    if (typeof value === 'string') {
      return { value, variables: mapVariableGroups(variableGroups ?? []) };
    }
    const existingValue = value as FieldMappingEditorValue;
    return {
      ...existingValue,
      variables: { ...mapVariableGroups(variableGroups ?? []), ...existingValue?.variables },
    };
  };

  const handleChange = (value: FieldMappingEditorValue) => {
    (actions.createChangeHandler as any)(field, contentKey)(value);
  };

  const initialValue = getFieldValue();

  const customShowFieldMappingSidebar = (props?: { variable?: FieldMappingFieldItem }) => {
    showStandardFieldMappingSidebar({
      editorRef: editorRef,
      variableGroups: variableGroups ?? [],
      fieldMappingId: field.id,
      fieldType: fieldType,
      label: sidebarLabel,
      isStandardSidebar: true,
      ...props,
    });
  };

  const FieldMappingEditor = shouldUseFieldMapping ? (
    <FieldMappingTextEditor
      ref={editorRef}
      defaultValue={initialValue}
      onChange={handleChange}
      hideLabel
      variableGroups={variableGroups}
      fieldMappingId={field.id}
      fieldType={fieldType}
      plaintext={plaintext}
      singleLine={singleLine}
      key={`field-mapping-${field.kind}-${field.id}`}
      customShowFieldMappingSidebar={customShowFieldMappingSidebar}
      isEditMode={isEditMode}
      actions={
        <Stack orientation="horizontal" space={2}>
          <IconButton
            icon={<IconAddSmall />}
            variant="blue-white"
            size="sm"
            onClick={() => customShowFieldMappingSidebar()}
            data-cy={`${field.kind}-field-mapping-open`}
          />
        </Stack>
      }
    />
  ) : null;

  return {
    shouldUseFieldMapping,
    FieldMappingEditor,
    editorRef,
    variableGroups,
  };
};
